import { DoublyLinkedList } from './doubly-linked-list'
export type CacheKey = string | symbol
export interface CacheOptions {
  /**
   * 缓存最大项数
   */
  maxItems?: number
  /**
   * 当使用indexedDB时，需要指定以下参数
   */
  dbName?: string
  /**
   * 当使用indexedDB时，需要指定以下参数
   */
  storeName?: string
  /**
   * 当使用indexedDB时，需要指定以下参数
   */
  dbVersion?: number
}
export interface CacheItem<T> {
  value: T
  timestamp: number
  expiry?: number
  expiryType?: 'absolute' | 'sliding'
}

export class Cache<T> {
  private adapter: Map<CacheKey, T>
  private maxItems: number
  private lruList: DoublyLinkedList<CacheKey>

  constructor({ maxItems = 999 }: CacheOptions) {
    if (maxItems <= 0 || !Number.isInteger(maxItems)) {
      throw new Error('maxItems must be a positive integer')
    }
    this.maxItems = maxItems
    this.adapter = new Map<CacheKey, T>()
    this.lruList = new DoublyLinkedList<CacheKey>()
  }

  private updateLRU(key: CacheKey) {
    this.lruList.add(key)
    if (this.lruList.size() > this.maxItems) {
      const oldestKey = this.lruList.getFirst()
      if (oldestKey) {
        this.lruList.remove(oldestKey)
        this.remove(oldestKey)
      }
    }
  }

  private isExpired(item: CacheItem<T>): boolean {
    if (!item.expiry) return false

    if (item.expiryType === 'sliding') {
      return Date.now() - item.timestamp > item.expiry
    }

    // 绝对过期时间
    return Date.now() > item.expiry
  }

  get(key: CacheKey): T | null {
    const item = this.adapter.get(key) as CacheItem<T> | null
    if (!item) return null

    if (this.isExpired(item)) {
      this.remove(key)
      return null
    }

    // 如果是动态过期时间，更新时间戳
    if (item.expiryType === 'sliding') {
      item.timestamp = Date.now()
      this.adapter.set(key, item as T)
    }

    this.updateLRU(key)
    return item.value
  }

  set(key: CacheKey, value: T, expiry?: number, expiryType: 'absolute' | 'sliding' = 'absolute') {
    const item: CacheItem<T> = {
      value,
      timestamp: Date.now(),
      expiry: expiry ? (expiryType === 'absolute' ? Date.now() + expiry : expiry) : undefined,
      expiryType,
    }
    this.adapter.set(key, item as T)
    this.updateLRU(key)
  }

  remove(key: CacheKey) {
    this.lruList.remove(key)
    this.adapter.delete(key)
  }

  async clear() {
    this.lruList.clear()
    return this.adapter.clear()
  }
}
